import { ArrowRight, FileSignature, FileUp, Loader2, ShieldCheck } from "lucide-react";
import { fmtDate, fmtMoney } from "../proposal/format";
import { Notice, PrimaryButton, Stat, StepHeader } from "./fields";

const ROUTES = [
  {
    icon: FileSignature,
    title: "Sign the Medicys Statement of Work",
    body: "Review and sign our standard Statement of Work online. The signed PDF reaches you by email within minutes.",
  },
  {
    icon: FileUp,
    title: "Use your own contract",
    body: "Tell us about the agreement your team will issue and upload the draft so we can review it against the proposal.",
  },
];

export function ConfirmStep({ state, d, onStart, busy, error }) {
  const net = state.totals?.net_total ?? d.sub_total;
  const company = state.client_company || d.client_company;
  return (
    <div className="flex flex-col gap-8" data-testid="commission-confirm">
      <StepHeader
        eyebrow="Step 1 of 5 · Confirm"
        title="Ready to go ahead?"
        intro="Confirming lets the Medicys team reserve the people and resources for this project. You then choose how the paperwork is handled. Both routes take only a few minutes, and nothing is final until the contract is in place."
      />

      <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
        <div className="px-6 sm:px-8 py-6 border-b border-stone-200 bg-stone-50/60">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-500">You are commissioning</p>
          <p className="font-serif text-2xl sm:text-3xl text-stone-900 leading-tight mt-1.5" data-testid="commission-confirm-ref">{d.project_reference}</p>
          <p className="text-sm text-stone-500 mt-1.5">Proposal #{d.proposal_number} for {company}</p>
        </div>
        <dl className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5 px-6 sm:px-8 py-6">
          <Stat label="Net value, excluding VAT" value={fmtMoney(net, "£", 2)} testid="commission-confirm-value" />
          <Stat
            label="Quote valid until"
            value={<span className={state.expired ? "text-amber-700" : ""}>{fmtDate(state.valid_until)}{state.expired ? " (expired)" : ""}</span>}
            mono={false}
            testid="commission-confirm-validity"
          />
          <Stat label="Prepared by" value={d.prepared_by ? `${d.prepared_by}, Medicys` : "Medicys"} mono={false} />
          <Stat label="Proposal date" value={fmtDate(d.proposal_date) || ""} mono={false} />
        </dl>
      </div>

      {state.expired && (
        <Notice tone="warning" title="This quote is 90 days or older" testid="commission-expired-note">
          The proposal was valid until {fmtDate(state.valid_until)}, so it cannot be commissioned until Medicys has confirmed that the costs still stand. When you press the button below, an email is sent to Medicys asking for that confirmation. Nothing is agreed and nothing else happens until {state.bd_name || "your Medicys contact"} comes back to you.
        </Notice>
      )}
      {state.costs_refreshed && (
        <Notice tone="success" title="Costs confirmed by Medicys" testid="commission-costs-refreshed-note">
          Medicys has reconfirmed the costs in this proposal. The quote is now valid until {fmtDate(state.valid_until)} and you can commission the project.
        </Notice>
      )}

      <div>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-3">What happens next</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {ROUTES.map(({ icon: Icon, title, body }) => (
            <div key={title} className="flex gap-4 rounded-xl border border-stone-200 bg-white p-5">
              <span className="w-10 h-10 rounded-md bg-[#1B4F8A]/8 text-[#1B4F8A] flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5" />
              </span>
              <div>
                <p className="font-medium text-stone-900">{title}</p>
                <p className="text-sm text-stone-600 leading-relaxed mt-1">{body}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-sm text-stone-500 mt-3">You choose between the two on the next screen.</p>
      </div>

      {error && <Notice tone="danger" testid="commission-error">{error}</Notice>}

      {!state.can_write ? (
        <Notice tone="info" testid="commission-readonly-note">
          Commissioning needs a verified client email address, so the button is not shown in this staff preview. Open the link with <code className="font-mono text-xs">?as=client</code> to run the flow as a client would.
        </Notice>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
          <PrimaryButton onClick={onStart} disabled={busy} testid="commission-start" size="lg">
            {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> One moment</>) : (<>{state.expired ? "Ask Medicys to confirm the costs" : "Yes, commission this project"} <ArrowRight className="w-4 h-4" /></>)}
          </PrimaryButton>
          <p className="flex items-start gap-2 text-xs text-stone-500 leading-relaxed max-w-sm">
            <ShieldCheck className="w-4 h-4 shrink-0 text-stone-400" />
            By continuing you confirm that you are authorised to commission work on behalf of {company}.
          </p>
        </div>
      )}
    </div>
  );
}

export function ExpiredRequested({ state }) {
  return (
    <div className="flex flex-col gap-6" data-testid="commission-expired-done">
      <StepHeader eyebrow="Request sent to Medicys" title="Thank you, we are checking the costs" />
      <Notice tone="warning">
        This proposal was valid until {fmtDate(state.valid_until)}, so it cannot be commissioned until the costs have been reconfirmed. An email has been sent to Medicys. {state.bd_name ? `${state.bd_name} will` : "Your Medicys contact will"} check the costs and email you, usually within one working day, either to confirm them or to send a refreshed proposal.
      </Notice>
      <p className="text-sm text-stone-500">There is nothing more for you to do for now. Once Medicys has confirmed the costs, this page unlocks and you can commission the project from here.</p>
    </div>
  );
}
